import { format } from 'date-fns'
import type { FC } from 'react'
import React, { useLayoutEffect, useRef, useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { EnumGlobalRoutes } from '@/app/router/routerConstants'
import Button from '@/compoments/common/Button'
import {
    selectListPagination,
    selectNotificationList,
} from '@/features/notifications/notificationsSlice'
import {
    useGetMyNotificationQuery,
    useMarkAllMyNotificationMutation,
    useMarkByIdMyNotificationMutation,
} from '@/services/notifications/notifications'
import type { INotification } from '@/types/notifications'

import BellIcon from '@/assets/img/ico-bell.svg?react'
import Close2Svg from '@/assets/img/ico-close-2.svg?react'
import InfoIcon from '@/assets/img/ico-info.svg?react'
import WarnIcon from '@/assets/img/ico-warn.svg?react'

export const getNotificationIcon = (type: string) => {
    if (type === 'info') return <InfoIcon />
    if (type === 'warn') return <WarnIcon />
    return <BellIcon />
}

export const NotificationDropdownMenu: FC = () => {
    const dispatch = useDispatch()
    const notificationList = useSelector(selectNotificationList)
    const pagination = useSelector(selectListPagination)
    const navigate = useNavigate()
    const [currentPage, setCurrentPage] = useState(1)
    const [markAsRead] = useMarkByIdMyNotificationMutation()
    const [markAllAsRead] = useMarkAllMyNotificationMutation()
    const { refetch } = useGetMyNotificationQuery(
        `page=${currentPage}&limit=10`,
        {
            skip: currentPage === 1,
        },
    )
    const hasMore = currentPage < (pagination?.maxPages || 2)

    // useEffect(() => {
    //     refetch()
    // }, [refetch, currentPage])

    const fetchMoreData = () => {
        if (hasMore) {
            setCurrentPage((prevPage) => prevPage + 1)
        }
    }

    const messagesRefs = useRef<Record<string, HTMLElement>>({})
    const previousVisibleUnreadIds = useRef<string[]>([])
    const containerRef = useRef<HTMLUListElement | null>(null)

    useLayoutEffect(() => {
        if (!containerRef.current) return

        const observer = new IntersectionObserver(
            (entries) => {
                const visibleUnreadIds = entries
                    .filter((entry) => entry.isIntersecting)
                    .map(
                        (entry) =>
                            entry.target.getAttribute('data-id') as string,
                    )
                    .filter(
                        (id) => !previousVisibleUnreadIds.current.includes(id),
                    )

                if (visibleUnreadIds.length > 0) {
                    Promise.allSettled(
                        visibleUnreadIds.map(async (id) => markAsRead(+id)),
                    )
                        .then((results) => {
                            const fulfilledIds = results
                                .filter(
                                    (result) => result.status === 'fulfilled',
                                )
                                .map((_, index) => visibleUnreadIds[index])

                            previousVisibleUnreadIds.current = [
                                ...previousVisibleUnreadIds.current,
                                ...fulfilledIds,
                            ]
                        })
                        .catch((err) => console.error(err))
                }
            },
            {
                root: containerRef.current,
                threshold: 0.5,
            },
        )

        Object.values(messagesRefs.current).forEach((el) => {
            if (el) {
                observer.observe(el)
            }
        })

        return () => {
            observer.disconnect()
            messagesRefs.current = {}
            previousVisibleUnreadIds.current = []
        }
    }, [notificationList])

    const handleClearAll = async () => {
        await markAllAsRead('')
    }

    return (
        <div className="modal-dropdown">
            <div className="modal-dropdown--header">
                <p className="modal-dropdown--title text--sm font-500">
                    Notifications (
                    {pagination?.total || notificationList?.length})
                </p>
                <div className="modal-dropdown--actions">
                    <button
                        onClick={handleClearAll}
                        className="btn color-primary text--sm font-500"
                    >
                        <span>Clear All</span>
                    </button>
                    <button
                        type="button"
                        className="btn btn--xxs btn--square btn--secondary-3 full-radius fix-dropdown-btn"
                    >
                        <span className="ico">
                            <Close2Svg />
                        </span>
                    </button>
                </div>
            </div>
            <div className="modal-dropdown--body">
                <ul
                    ref={containerRef}
                    className="list-notifications"
                    id="scrollableNotificationList"
                    style={{
                        overflow: 'auto',
                    }}
                >
                    <InfiniteScroll
                        dataLength={notificationList?.length || 0}
                        next={fetchMoreData}
                        hasMore={hasMore}
                        scrollableTarget="scrollableNotificationList"
                        loader={<></>}
                    >
                        {notificationList?.map(
                            (notification: INotification, index: number) => (
                                <li
                                    key={index}
                                    className="item-notification"
                                    data-id={notification.id}
                                    ref={
                                        notification.isRead
                                            ? null
                                            : (el) => {
                                                  if (el)
                                                      messagesRefs.current[
                                                          notification.id
                                                      ] = el
                                              }
                                    }
                                >
                                    <div className="btn-notification">
                                        <div className="notification-header">
                                            <div className="notification-title">
                                                <span className="noti-ico ico">
                                                    {getNotificationIcon(
                                                        notification.type,
                                                    )}
                                                </span>
                                                <p className="noti-title">
                                                    {notification.title}
                                                </p>
                                            </div>
                                            <span className="noti-time">
                                                {notification?.createdAt
                                                    ? format(
                                                          new Date(
                                                              notification?.createdAt,
                                                          ),
                                                          'HH:mm',
                                                      )
                                                    : ''}
                                            </span>
                                        </div>
                                        <div className="notification-body">
                                            <p className="noti-description">
                                                {notification.description}
                                            </p>
                                        </div>
                                    </div>
                                </li>
                            ),
                        )}
                    </InfiniteScroll>
                </ul>
                <div className="flex-group flex-group--xs w-full">
                    <Button
                        type="submit"
                        className="w-full"
                        onClick={() => navigate(EnumGlobalRoutes.NOTIFICATIONS)}
                        size="sm"
                        variant="primary-gradient"
                    >
                        <span>View All</span>
                    </Button>
                </div>
            </div>
        </div>
    )
}
